/**
 * Shared constants for dashboard top bar and split-flap layout.
 * Used by DashboardNav and NavFlap portals for consistent height and iOS safe area.
 */

/** Toolbar height when centered split-flap is shown (single row; tile clamp 52–88px + 8px padding, min 72px). Must match SplitFlap structure. */
export const TOOLBAR_HEIGHT_WITH_FLAP =
  "max(72px, calc(8px + clamp(52px, 6.5vw, 88px)))";

/** Toolbar height when narrow two-line flap is shown (two stacked rows; smaller tiles). Used for xs/mobile when role flaps use two-line layout. */
export const TOOLBAR_HEIGHT_WITH_FLAP_NARROW =
  "max(100px, calc(16px + 2 * clamp(20px, 5.2vw, 38px)))";

/**
 * professional-chartmind AppBar CHARTMIND flap only: ~0.5× standard tile size (more discrete).
 * Must stay in sync with ChartMindSplitFlap compactNav tile clamps + frame padding.
 */
export const TOOLBAR_HEIGHT_CHARTMIND_NAV_FLAP =
  "max(36px, calc(4px + clamp(26px, 3.25vw, 44px)))";
export const TOOLBAR_HEIGHT_CHARTMIND_NAV_FLAP_NARROW =
  "max(50px, calc(8px + 2 * clamp(10px, 2.6vw, 19px)))";

/** Patient MasterChart AppBar: same ~0.5× tile geometry as ChartMind compact nav. */
export const TOOLBAR_HEIGHT_MASTERCHART_NAV_FLAP = TOOLBAR_HEIGHT_CHARTMIND_NAV_FLAP;
export const TOOLBAR_HEIGHT_MASTERCHART_NAV_FLAP_NARROW = TOOLBAR_HEIGHT_CHARTMIND_NAV_FLAP_NARROW;

/** facility / f2 AppBar SavoirCare flap: ~0.5× former full-size tiles; same bar geometry as ChartMind nav. */
export const TOOLBAR_HEIGHT_SAVOIRCARE_NAV_FLAP = TOOLBAR_HEIGHT_CHARTMIND_NAV_FLAP;
export const TOOLBAR_HEIGHT_SAVOIRCARE_NAV_FLAP_NARROW = TOOLBAR_HEIGHT_CHARTMIND_NAV_FLAP_NARROW;

/**
 * Reserved AppBar row min height for professional-chartmind `AdCarousel` strip
 * (logo max 40px + button + padding — see AdCarousel / AdSlide).
 */
export const TOOLBAR_CHARTMIND_AD_CAROUSEL_STRIP_MIN_HEIGHT = "96px";

/**
 * Extra vertical space from `AdCarousel` `appBarRow` outer `Box` (`py: 0.5` ≈ 4px × 2 at default MUI spacing).
 * Layout totals must include this or the drawer header / main padding sit short vs the real AppBar row.
 */
export const TOOLBAR_CHARTMIND_AD_CAROUSEL_STRIP_MUI_PY_OUTSET_PX = 8;

/** Use for AppBar carousel row, drawer header, and main `paddingTop` — matches rendered strip including outer padding. */
export const TOOLBAR_CHARTMIND_AD_CAROUSEL_STRIP_OUTER_MIN_HEIGHT = `calc(${TOOLBAR_CHARTMIND_AD_CAROUSEL_STRIP_MIN_HEIGHT} + ${TOOLBAR_CHARTMIND_AD_CAROUSEL_STRIP_MUI_PY_OUTSET_PX}px)`;

/** iOS safe area inset top for notch/status bar. Use in paddingTop or top. */
export const SAFE_AREA_INSET_TOP = "env(safe-area-inset-top, 0px)";

/** Main content top offset when flap role on desktop (sm+): bar height + safe area. Matches DashboardNav. */
export const MAIN_PADDING_TOP_WITH_FLAP = `calc(${TOOLBAR_HEIGHT_WITH_FLAP} + ${SAFE_AREA_INSET_TOP})`;

/** Reserved width (px) on each side of the toolbar so menu and right icon stay visible; split-flap portal max-width = 100vw minus this. */
const FLAP_SIDE_RESERVE_PX = 72;
/** Max width for the centered split-flap portal so it doesn't cover menu bar or right-side icon. */
export const FLAP_PORTAL_MAX_WIDTH = `calc(100vw - ${FLAP_SIDE_RESERVE_PX * 2}px)`;

/** SavoirCare flap uses this share of the available horizontal space (portal max width). 69.2% = 0.692. */
export const SAVOIRCARE_FLAP_WIDTH_PERCENT = 69.2;
/** Width of the SavoirCare SFD: 69.2% of (100vw - 144px). Approx: 69.2vw - 100px. */
export const SAVOIRCARE_FLAP_WIDTH = `calc(${SAVOIRCARE_FLAP_WIDTH_PERCENT}vw - 100px)`;

/** Height (px) of the icon row when menu/bell are placed below the flap on mobile (iOS). */
export const MOBILE_ICONS_ROW_HEIGHT_PX = 48;
/** Main content top offset when flap role on mobile (xs): safe area + flap height + icons row below. */
export const MAIN_PADDING_TOP_MOBILE_FLAP_ICONS_BELOW = `calc(${SAFE_AREA_INSET_TOP} + ${TOOLBAR_HEIGHT_WITH_FLAP_NARROW} + ${MOBILE_ICONS_ROW_HEIGHT_PX}px)`;

/** Viewport width (px) below which patient MasterChart flap uses two-line MASTER/CHART; above this use single-line MASTERCHART. */
export const PATIENT_FLAP_NARROW_BREAKPOINT_PX = 400;
/** Mobile xs: full-size single-line flap (e.g. SavoirCare) + icons row below. */
export const MAIN_PADDING_TOP_MOBILE_FULL_FLAP_SINGLE_LINE = `calc(${SAFE_AREA_INSET_TOP} + ${TOOLBAR_HEIGHT_WITH_FLAP} + ${MOBILE_ICONS_ROW_HEIGHT_PX}px)`;
/** Mobile xs: compact SavoirCare (facility / f2) single-line + icons row. */
export const MAIN_PADDING_TOP_MOBILE_SAVOIRCARE_FLAP_SINGLE_LINE = `calc(${SAFE_AREA_INSET_TOP} + ${TOOLBAR_HEIGHT_SAVOIRCARE_NAV_FLAP} + ${MOBILE_ICONS_ROW_HEIGHT_PX}px)`;
/** Mobile xs: compact SavoirCare with narrow tiles (viewport below sm) + icons row. */
export const MAIN_PADDING_TOP_MOBILE_SAVOIRCARE_FLAP_NARROW = `calc(${SAFE_AREA_INSET_TOP} + ${TOOLBAR_HEIGHT_SAVOIRCARE_NAV_FLAP_NARROW} + ${MOBILE_ICONS_ROW_HEIGHT_PX}px)`;
/** Main content top offset when patient on mobile (xs) with single-line compact MasterChart flap: safe area + bar + icons row. */
export const MAIN_PADDING_TOP_MOBILE_PATIENT_SINGLE_LINE = `calc(${SAFE_AREA_INSET_TOP} + ${TOOLBAR_HEIGHT_MASTERCHART_NAV_FLAP} + ${MOBILE_ICONS_ROW_HEIGHT_PX}px)`;
/** Patient mobile two-line MASTER/CHART + icons below. */
export const MAIN_PADDING_TOP_MOBILE_PATIENT_NARROW = `calc(${SAFE_AREA_INSET_TOP} + ${TOOLBAR_HEIGHT_MASTERCHART_NAV_FLAP_NARROW} + ${MOBILE_ICONS_ROW_HEIGHT_PX}px)`;

/** Viewport width (px) below which professional-chartmind flap uses two-line CHART/MIND; above this use single-line CHARTMIND. Must match ChartMindSplitFlap CHARTMIND_NARROW_MAX. */
export const CHARTMIND_FLAP_NARROW_BREAKPOINT_PX = 380;
/** MSI³ strip above CHARTMIND on mobile AppBar (xs); keep in sync with `DashboardNav` MSI³ row. */
export const MOBILE_CHARTMIND_MSI3_APPBAR_ROW_PX = 44;
/** Main content top offset when professional-chartmind on mobile (xs) with single-line flap: safe area + compact bar + icons row. */
export const MAIN_PADDING_TOP_MOBILE_CHARTMIND_SINGLE_LINE = `calc(${SAFE_AREA_INSET_TOP} + ${MOBILE_CHARTMIND_MSI3_APPBAR_ROW_PX}px + ${TOOLBAR_HEIGHT_CHARTMIND_NAV_FLAP} + ${MOBILE_ICONS_ROW_HEIGHT_PX}px)`;
/** professional-chartmind mobile two-line CHART/MIND row + icons below. */
export const MAIN_PADDING_TOP_MOBILE_CHARTMIND_NARROW = `calc(${SAFE_AREA_INSET_TOP} + ${MOBILE_CHARTMIND_MSI3_APPBAR_ROW_PX}px + ${TOOLBAR_HEIGHT_CHARTMIND_NAV_FLAP_NARROW} + ${MOBILE_ICONS_ROW_HEIGHT_PX}px)`;
/** Desktop sm+ main offset under compact ChartMind AppBar flap. */
export const MAIN_PADDING_TOP_WITH_CHARTMIND_FLAP = `calc(${TOOLBAR_HEIGHT_CHARTMIND_NAV_FLAP} + ${SAFE_AREA_INSET_TOP})`;
/** Desktop sm+ main offset: ChartMind flap row + ad carousel strip + safe area. */
export const MAIN_PADDING_TOP_WITH_CHARTMIND_FLAP_AND_CAROUSEL = `calc(${TOOLBAR_HEIGHT_CHARTMIND_NAV_FLAP} + ${TOOLBAR_CHARTMIND_AD_CAROUSEL_STRIP_OUTER_MIN_HEIGHT} + ${SAFE_AREA_INSET_TOP})`;

/**
 * Permanent drawer paper height (sm+) when ChartMind AppBar spans the full viewport width.
 * Must stay in sync with {@link MAIN_PADDING_TOP_WITH_CHARTMIND_FLAP_AND_CAROUSEL} as `100dvh - offset`.
 */
export const PERMANENT_DRAWER_PAPER_HEIGHT_SM_PROFESSIONAL_CHARTMIND = `calc(100dvh - ${TOOLBAR_HEIGHT_CHARTMIND_NAV_FLAP} - ${TOOLBAR_CHARTMIND_AD_CAROUSEL_STRIP_OUTER_MIN_HEIGHT} - ${SAFE_AREA_INSET_TOP})`;
/** Desktop sm+ main offset under compact SavoirCare AppBar flap (facility / f2). */
export const MAIN_PADDING_TOP_WITH_SAVOIRCARE_NAV_FLAP = `calc(${TOOLBAR_HEIGHT_SAVOIRCARE_NAV_FLAP} + ${SAFE_AREA_INSET_TOP})`;
/** Extra top offset (px) for professional-chartmind on mobile so main content stays fully below the bar on iOS (notch, rounding). */
export const MAIN_PADDING_TOP_MOBILE_CHARTMIND_BUFFER_PX = 16;

/** Mobile xs: ChartMind single-line flap + icons row + ad carousel strip + buffer. */
export const MAIN_PADDING_TOP_MOBILE_CHARTMIND_SINGLE_LINE_AND_CAROUSEL = `calc(${SAFE_AREA_INSET_TOP} + ${MOBILE_CHARTMIND_MSI3_APPBAR_ROW_PX}px + ${TOOLBAR_HEIGHT_CHARTMIND_NAV_FLAP} + ${MOBILE_ICONS_ROW_HEIGHT_PX}px + ${TOOLBAR_CHARTMIND_AD_CAROUSEL_STRIP_OUTER_MIN_HEIGHT} + ${MAIN_PADDING_TOP_MOBILE_CHARTMIND_BUFFER_PX}px)`;
/** Mobile xs: ChartMind narrow two-line flap + icons + carousel strip + buffer. */
export const MAIN_PADDING_TOP_MOBILE_CHARTMIND_NARROW_AND_CAROUSEL = `calc(${SAFE_AREA_INSET_TOP} + ${MOBILE_CHARTMIND_MSI3_APPBAR_ROW_PX}px + ${TOOLBAR_HEIGHT_CHARTMIND_NAV_FLAP_NARROW} + ${MOBILE_ICONS_ROW_HEIGHT_PX}px + ${TOOLBAR_CHARTMIND_AD_CAROUSEL_STRIP_OUTER_MIN_HEIGHT} + ${MAIN_PADDING_TOP_MOBILE_CHARTMIND_BUFFER_PX}px)`;

/**
 * professional-chartmind drawer top band (border-box height including `paddingTop: SAFE_AREA_INSET_TOP`).
 * Use with `alignItems: flex-start` so MSI³ sits under the safe inset instead of vertically centered.
 */
export const DRAWER_HEADER_CHARTMIND_SM_HEIGHT = `calc(${SAFE_AREA_INSET_TOP} + ${TOOLBAR_HEIGHT_CHARTMIND_NAV_FLAP} + ${TOOLBAR_CHARTMIND_AD_CAROUSEL_STRIP_OUTER_MIN_HEIGHT})`;
export const DRAWER_HEADER_CHARTMIND_XS_HEIGHT_SINGLE = `calc(${SAFE_AREA_INSET_TOP} + ${TOOLBAR_HEIGHT_CHARTMIND_NAV_FLAP} + ${MOBILE_ICONS_ROW_HEIGHT_PX}px + ${TOOLBAR_CHARTMIND_AD_CAROUSEL_STRIP_OUTER_MIN_HEIGHT})`;
export const DRAWER_HEADER_CHARTMIND_XS_HEIGHT_NARROW = `calc(${SAFE_AREA_INSET_TOP} + ${TOOLBAR_HEIGHT_CHARTMIND_NAV_FLAP_NARROW} + ${MOBILE_ICONS_ROW_HEIGHT_PX}px + ${TOOLBAR_CHARTMIND_AD_CAROUSEL_STRIP_OUTER_MIN_HEIGHT})`;

/**
 * Patient / p2: purple satin brand bar — ~120px title/SFD row per docs/BRAND_BAR_ROLE_PAGE_HEADERS.md (symptom-checker bar system).
 */
export const PATIENT_PURPLE_BRAND_BAR_HEIGHT_PX = 120;

/**
 * Full patient/p2 AppBar from viewport top: title row + ad carousel strip (same outer height as ChartMind) + safe area.
 * `AdCarousel` resolves audience via `getMapCarouselViewerSegment(role)`; `patient` / `p2` → patient-targeted slides.
 */
export const PATIENT_PURPLE_BAR_TOTAL_HEIGHT = `calc(${PATIENT_PURPLE_BRAND_BAR_HEIGHT_PX}px + ${TOOLBAR_CHARTMIND_AD_CAROUSEL_STRIP_OUTER_MIN_HEIGHT} + ${SAFE_AREA_INSET_TOP})`;

/** Drawer paper height when it starts below the patient brand bar (includes carousel row). */
export const PATIENT_PURPLE_DRAWER_PAPER_HEIGHT = `calc(100dvh - ${PATIENT_PURPLE_BRAND_BAR_HEIGHT_PX}px - ${TOOLBAR_CHARTMIND_AD_CAROUSEL_STRIP_OUTER_MIN_HEIGHT} - ${SAFE_AREA_INSET_TOP})`;

/** Main content `paddingTop` under patient/p2 purple brand bar + carousel. */
export const MAIN_PADDING_TOP_PATIENT_PURPLE = PATIENT_PURPLE_BAR_TOTAL_HEIGHT;

/** Mobile xs: patient compact single-line MasterChart + icons row + ad carousel + buffer (no extra MSI row — MSI is in flap grid). */
export const MAIN_PADDING_TOP_MOBILE_PATIENT_SINGLE_LINE_AND_CAROUSEL = `calc(${SAFE_AREA_INSET_TOP} + ${TOOLBAR_HEIGHT_MASTERCHART_NAV_FLAP} + ${MOBILE_ICONS_ROW_HEIGHT_PX}px + ${TOOLBAR_CHARTMIND_AD_CAROUSEL_STRIP_OUTER_MIN_HEIGHT} + ${MAIN_PADDING_TOP_MOBILE_CHARTMIND_BUFFER_PX}px)`;

/** Mobile xs: patient two-line MASTER/CHART + icons + carousel + buffer. */
export const MAIN_PADDING_TOP_MOBILE_PATIENT_NARROW_AND_CAROUSEL = `calc(${SAFE_AREA_INSET_TOP} + ${TOOLBAR_HEIGHT_MASTERCHART_NAV_FLAP_NARROW} + ${MOBILE_ICONS_ROW_HEIGHT_PX}px + ${TOOLBAR_CHARTMIND_AD_CAROUSEL_STRIP_OUTER_MIN_HEIGHT} + ${MAIN_PADDING_TOP_MOBILE_CHARTMIND_BUFFER_PX}px)`;

/** Temporary drawer paper height (xs) below patient mobile chrome — matches `MAIN_PADDING_TOP_MOBILE_PATIENT_*_AND_CAROUSEL`. */
export const PATIENT_PURPLE_DRAWER_PAPER_HEIGHT_MOBILE_SINGLE_LINE = `calc(100dvh - (${MAIN_PADDING_TOP_MOBILE_PATIENT_SINGLE_LINE_AND_CAROUSEL}))`;
export const PATIENT_PURPLE_DRAWER_PAPER_HEIGHT_MOBILE_NARROW = `calc(100dvh - (${MAIN_PADDING_TOP_MOBILE_PATIENT_NARROW_AND_CAROUSEL}))`;

/** Advertiser: dark blue satin AppBar strip height (same geometry as ChartMind; matches `DarkBlueSatinBarBackgroundSvg` / #1B4584). */
export const ADVERTISER_GOLD_BAR_HEIGHT_PX = 120;

/** Full fixed brand band from viewport top: bar + notch/safe area (backdrop, AppBar, SFD portal). */
export const ADVERTISER_GOLD_BAR_TOTAL_HEIGHT = `calc(${ADVERTISER_GOLD_BAR_HEIGHT_PX}px + ${SAFE_AREA_INSET_TOP})`;

/**
 * Drawer paper height when it starts below the advertiser brand band (same vertical band as main `paddingTop`).
 * Keeps permanent and temporary advertiser drawers aligned with the main display area.
 */
export const ADVERTISER_DRAWER_PAPER_HEIGHT = `calc(100dvh - ${ADVERTISER_GOLD_BAR_HEIGHT_PX}px - ${SAFE_AREA_INSET_TOP})`;

/** Toolbar / header icons and title on advertiser dark blue satin bar (white for contrast). */
export const ADVERTISER_GOLD_BAR_FOREGROUND = "common.white";

/** Re-export: defined in a small module so drawer HMR chunks always resolve the binding. */
export { LEGACY_PROMOTIONS_WATERMARK_CHIP_SX } from "./legacyPromotionsWatermarkChipSx";

/** llm-manager-global: dark blue satin AppBar height (same strip geometry as advertiser brand bar). */
export const LLM_GLOBAL_MANAGER_BAR_HEIGHT_PX = 120;

/** Full fixed blue band from viewport top: bar + notch/safe area. */
export const LLM_GLOBAL_MANAGER_BAR_TOTAL_HEIGHT = `calc(${LLM_GLOBAL_MANAGER_BAR_HEIGHT_PX}px + ${SAFE_AREA_INSET_TOP})`;

/** Drawer paper height below the global LLM brand bar. */
export const LLM_GLOBAL_MANAGER_DRAWER_PAPER_HEIGHT = `calc(100dvh - ${LLM_GLOBAL_MANAGER_BAR_HEIGHT_PX}px - ${SAFE_AREA_INSET_TOP})`;

/** iOS / xs: temporary drawer below stacked ChartMind/pro2 AppBar (flap + icons + carousel). */
export const CHARTMIND_DRAWER_PAPER_HEIGHT_MOBILE_SINGLE_LINE_AND_CAROUSEL = `calc(100dvh - (${MAIN_PADDING_TOP_MOBILE_CHARTMIND_SINGLE_LINE_AND_CAROUSEL}))`;
export const CHARTMIND_DRAWER_PAPER_HEIGHT_MOBILE_NARROW_AND_CAROUSEL = `calc(100dvh - (${MAIN_PADDING_TOP_MOBILE_CHARTMIND_NARROW_AND_CAROUSEL}))`;

/** Bottom inset for scrollable dashboard main card on phones (home indicator). */
export const MOBILE_MAIN_CARD_PADDING_BOTTOM_SAFE = `max(12px, env(safe-area-inset-bottom, 0px))`;
